import { buildComparePageURI } from '@v4/compare/utils';
import { navigateTo } from '@v4/http/request';
import compareModalHandler from '@v4/common/components/mixins/compare-modal-handler';
import { isMobile } from '@v4/common/utils/media-query';

/**
 * This mixin should be imported with also dkModalAware one.
 */
export const modalServiceCompareVehicle = {
    mixins: [compareModalHandler],
    methods: {
        /**
         * Show compare vehicle modal
         *
         * @param {object|null} vehicleToEdit - Selected vehicle
         * @param {string} vehicleToEdit.make - Make name
         * @param {string} vehicleToEdit.model - Model name
         * @param {string} vehicleToEdit.version - Version name
         * @param {object|null} startingVehicle - Starting vehicle data
         * @param {string} startingVehicle.make - Make name
         * @param {string} startingVehicle.model - Model name
         * @param {string} startingVehicle.version - Version name
         * @param {string[]} selectedVehicles - List of submodelCommercialUrlCode
         * @param {string} modalTitle - Modal title
         * @param {string} compareButtonTitle - Modal button text
         * @param {boolean} isOpenedFromComparePage - If the modal was opened from the compare page
         */
        async openCompareVehicleModal(
            vehicleToEdit = null,
            startingVehicle = null,
            selectedVehicles = [],
            modalTitle = '',
            compareButtonTitle = '',
            isOpenedFromComparePage = false
        ) {
            return import(
                /* webpackChunkName: "CompareVehicleModal-vue" */ '@v4/compare/components/organisms/CompareVehicleModal/CompareVehicleModal.vue'
            ).then(
                async ({ default: CompareVehicleModal }) =>
                    await this.showModal(
                        CompareVehicleModal,
                        {
                            vehicleToEdit,
                            startingVehicle,
                            selectedVehicles,
                            modalTitle,
                            compareButtonTitle,
                            isOpenedFromComparePage,
                        },
                        {
                            name: 'compare-vehicle-modal',
                            height: 'auto',
                            classes: 'modal--fullscreen_medium',
                            scrollable: true,
                            width: 448,
                        },
                        {},
                        {
                            disableBodyScroll: false,
                        }
                    )
            );
        },
        /**
         * Show compare vehicle modal and handle button/vehicle click
         *
         * @param {object|null} vehicleToEdit - Selected vehicle
         * @param {string} vehicleToEdit.make - Make name
         * @param {string} vehicleToEdit.model - Model name
         * @param {string} vehicleToEdit.version - Version name
         * @param {object|null} data - Starting vehicle data
         * @param {string} data.make - Make name
         * @param {string} data.model - Model name
         * @param {string} data.version - Version name
         * @param {string[]} selectedVehicles - List of submodelCommercialUrlCode
         * @param {string} modalTitle - Modal title
         * @param {string} compareButtonTitle - Modal button text
         */
        async openCompareVehicleModalAndHandleClick(
            vehicleToEdit = null,
            data = null,
            selectedVehicles = [],
            modalTitle = '',
            compareButtonTitle = ''
        ) {
            const starting = data
                ? {
                    make: data.make || data.makeUrlCode,
                    model: data.model || data.submodelCommercialUrlCode,
                    submodelId: data.submodelId || data.modelId,
                    version: data.vehicleHistoricalId || data.versionHistoricalId || data.version || data.versionId,
                }
                : null;

            const vehicle = await this.openCompareVehicleModal(
                vehicleToEdit,
                starting,
                selectedVehicles,
                modalTitle,
                compareButtonTitle
            );

            // Cleanup properties to accommodate changes in the APIs
            delete vehicle.vehicleHistoricalId;
            delete vehicle.version;
            delete starting.submodelId;
            delete starting.version;

            if (vehicle) {
                const url = buildComparePageURI(this.buildComparePageParamsList([starting, vehicle]));

                navigateTo(url);
            }
        },
    },
};

export const modalServiceOfferBenefits = {
    methods: {
        async openOfferBenefitsModal(item, onSelect) {
            return import(
                /* webpackChunkName: "OfferBenefitsModal-vue" */ '@v4/multi-section/components/organisms/OfferBenefitsModal.vue'
            ).then(
                async ({ default: OfferBenefitsModal }) =>
                    await this.showModal(
                        OfferBenefitsModal,
                        {
                            item,
                            onSelect,
                        },
                        {
                            name: 'offer-benefits-modal',
                            height: 'auto',
                            classes: 'modal--fullscreen_medium',
                            scrollable: true,
                            width: 448,
                        },
                        {},
                        {
                            disableBodyScroll: false,
                        }
                    )
            );
        },
    },
};

export const modalIncentiveBanner = {
    methods: {
        async openIncentiveModal() {
            return import(
                /* webpackChunkName: "IncentiveModal-vue" */ '@v4/common/components/molecules/IncentiveModal.vue'
            ).then(
                async ({ default: IncentiveModal }) =>
                    await this.showModal(
                        IncentiveModal,
                        {},
                        {
                            name: 'incentive-banner-modal',
                            height: 'auto',
                            classes: 'modal--fullscreen_medium',
                            scrollable: true,
                            width: 515,
                        },
                        {},
                        {
                            disableBodyScroll: false,
                        }
                    )
            );
        },
    },
};

export const modalEmissionConsumption = {
    methods: {
        async openEmissionConsumptionModal(modelName, detailsEmissionConsumption) {
            return import(
                /* webpackChunkName: "EmissionConsumptionModal-vue" */ '@v4/common/components/molecules/EmissionConsumptionModal/EmissionConsumptionModal.vue'
            ).then(
                async ({ default: EmissionConsumptionModal }) =>
                    await this.showModal(
                        EmissionConsumptionModal,
                        {
                            title: modelName,
                            items: detailsEmissionConsumption,
                        },
                        {
                            name: 'emission-consumption-modal',
                            height: 'auto',
                            classes: 'modal--fullscreen_medium',
                            scrollable: false,
                            width: 515,
                        },
                        {},
                        {
                            disableBodyScroll: false,
                        }
                    )
            );
        },
    },
};

export const modalCarConfigurator = {
    methods: {
        async openCarConfiguratorModal(defaultSelectedOptions, onSubmit, gearboxes, colors) {
            return import(
                /* webpackChunkName: "ConfigureCarModal-vue" */ '@v4/multi-section/components/molecules/ConfigureCarModal.vue'
            ).then(
                async ({ default: ConfigureCarModal }) =>
                    await this.showModal(
                        ConfigureCarModal,
                        {
                            defaultSelectedOptions,
                            onSubmit,
                            gearboxes,
                            colors,
                        },
                        {
                            name: 'car-configurator-modal',
                            height: 'auto',
                            classes: 'modal--fullscreen_medium',
                            scrollable: false,
                            width: 515,
                        },
                        {},
                        {
                            disableBodyScroll: false,
                        }
                    )
            );
        },
    },
};

export const modalServiceLeadForm = {
    methods: {
        async openLeadFormModal(props = {}, options = {}, events = {}) {
            return import(
                /* webpackChunkName: "LeadFormModal-vue" */ '@v4/common/components/organisms/LeadFormModal.vue'
            ).then(
                async ({ default: LeadFormModal }) =>
                    await this.showModal(
                        LeadFormModal,
                        props,
                        {
                            name: 'lead-form-modal',
                            height: 'auto',
                            classes: 'modal--fullscreen_medium',
                            width: 680,
                            scrollable: true,
                            ...options,
                        },
                        events
                    )
            );
        },
    },
};

export const modalServiceDealerSelection = {
    methods: {
        async openDealerSelectionModal(props = {}, options = {}, events = {}) {
            return import(
                /* webpackChunkName: "DealerSelectionModal-vue" */ '@/js/common/components/organisms/DealerSelection/DealerSelectionModal.vue'
            ).then(
                async ({ default: DealerSelectionModal }) =>
                    await this.showModal(
                        DealerSelectionModal,
                        props,
                        {
                            name: 'delear-selection-modal',
                            height: 'auto',
                            classes: 'modal--fullscreen_medium',
                            width: 680,
                            scrollable: true,
                            clickToClose: false,
                            ...options,
                        },
                        events
                    )
            );
        },
    },
};

export const modalServiceQuotesPromo = {
    methods: {
        async openQuotesPromoModal(props = {}, options = {}, events = {}) {
            return import(
                /* webpackChunkName: "QuotesPromoModal-vue" */ '@v4/quotes/components/molecules/QuotesPromoModal.vue'
            ).then(
                async ({ default: QuotesPromoModal }) =>
                    await this.showModal(
                        QuotesPromoModal,
                        props,
                        {
                            name: 'quotes-promo-modal',
                            height: 'auto',
                            classes: 'modal--fullscreen_medium',
                            width: 450,
                            scrollable: true,
                            ...options,
                        },
                        events
                    )
            );
        },
    },
};

export const modalInactivity = {
    methods: {
        openInactivityModal(props = {}, options = {}, events = {}) {
            return import(
                /* webpackChunkName: "InactivityModal-vue" */ '@v4/search/components/molecules/InactivityModal.vue'
            ).then(({ default: InactivityModal }) =>
                this.showModal(
                    InactivityModal,
                    props,
                    {
                        name: 'inactivity-modal',
                        height: 'auto',
                        width: 450,
                        ...options,
                    },
                    events
                )
            );
        },
    },
};

export const modalWizardCarConfigurator = {
    methods: {
        async openWizardCarConfiguratorModal(props = {}, options = {}, events = {}) {
            return import(
                /* webpackChunkName: "WizardConfigureCarModal-vue" */ '@v4/common/components/organisms/WizardConfigureCarModal/WizardConfigureCarModal.vue'
            ).then(
                async ({ default: WizardConfigureCarModal }) =>
                    await this.showModal(
                        WizardConfigureCarModal,
                        props,
                        {
                            name: 'wizard-car-configurator',
                            height: 'auto',
                            classes: 'modal--fullscreen_medium',
                            width: 450,
                            scrollable: true,
                            ...options,
                        },
                        events,
                        {
                            disableBodyScroll: false,
                        }
                    )
            );
        },
    },
};

export const modalClickToCall = {
    methods: {
        async openClickToCallModal(props = {}) {
            return import(
                /* webpackChunkName: "DkClickToCallModal-vue" */ '@v4/common/components/organisms/click-to-call/DkClickToCallModal.vue'
            ).then(
                async ({ default: DkClickToCallModal }) =>
                    await this.showModal(
                        DkClickToCallModal,
                        {
                            ...props,
                        },
                        {
                            name: 'dk-click-to-call-modal',
                            height: 'auto',
                            width: 450,
                        }
                    )
            );
        },
    },
};

export const modalOfferDetails = {
    methods: {
        async openmodalOfferDetails(props = {}) {
            return import(
                /* webpackChunkName: "DkOfferDetailsModalRevamp-vue" */ '@v4/common/components/molecules/DkOfferDetailsModalRevamp.vue'
            ).then(
                async ({ default: DkOfferDetailsModalRevamp }) =>
                    await this.showModal(
                        DkOfferDetailsModalRevamp,
                        {
                            offer: props.offer,
                            ...(props.isHomepageModal && { isHomepageModal: props.isHomepageModal }),
                            isMobile: isMobile(),
                        },
                        {
                            name: 'dk-offer-details-modal-revamp',
                            width: 644,
                            height: isMobile() ? '100%' : 'auto',
                        }
                    )
            );
        },
    },
};

export const modalWizardLeadForm = {
    methods: {
        async openWizardLeadFormModal(props = {}, setSelectedOffer) {
            const isMobileScreen = isMobile();
            return import(
                /* webpackChunkName: "DkWizardLeadFormModal-vue" */ '@v4/common/components/organisms/DkWizardLeadFormModal'
            ).then(
                async ({ DkWizardLeadFormModal }) =>
                    await this.showModal(
                        DkWizardLeadFormModal,
                        {
                            ...props,
                            isMobile: isMobileScreen,
                            ...(setSelectedOffer && { setSelectedOffer }),
                        },
                        {
                            name: 'dk-wizard-lead-form-modal',
                            width: 680,
                            height: isMobileScreen ? 'auto' : '100%',
                            ...(!isMobileScreen && {
                                adaptive: true,
                                maxHeight: 800,
                            }),
                            ...(isMobileScreen && { scrollable: true }),
                        },
                        {},
                        {
                            disableBodyScroll: false,
                        }
                    )
            );
        },
    },
};

export const modalOneStepLeadForm = {
    methods: {
        async openOneStepLeadFormModal(props = {}) {
            const isMobileScreen = isMobile();
            return import(
                /* webpackChunkName: "DkOneStepLeadFormModal-vue" */ '@v4/common/components/organisms/DkWizardLeadFormModal'
            ).then(
                async ({ DkOneStepLeadFormModal }) =>
                    await this.showModal(
                        DkOneStepLeadFormModal,
                        {
                            ...props,
                            isMobile: isMobileScreen,
                        },
                        {
                            name: 'dk-one-step-lead-form-modal',
                            width: 680,
                            height: isMobileScreen ? '100%' : 'auto',
                            ...(!isMobileScreen && {
                                adaptive: true,
                                maxHeight: 800,
                            }),
                            ...(isMobileScreen && { scrollable: true }),
                        },
                        {},
                        {
                            disableBodyScroll: false,
                        }
                    )
            );
        },
    },
};
